import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { INSPECTIONS_LIST, INSPECTION_DELETE } from '../../graphql/inspections';
import Pagination from '../../components/Pagination';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton, 
  Button, 
  TableSortLabel, 
  Box, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Typography,
  Chip,
  Avatar,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Tooltip,
  CircularProgress,
  MenuItem
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  CheckCircle,
  Warning,
  Schedule,
  DirectionsCar,
  ExpandMore as ExpandMoreIcon,
  Build,
  Category,
  Title,
  Person,
  CalendarToday,
  Assignment
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import Collapse from '@mui/material/Collapse';

// Work order categories for display
const workOrderCategories = [
  { value: 'All', label: 'All Categories' },
  { value: 'mechanical', label: 'Mechanical' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'body_repair', label: 'Body Repair' },
  { value: 'tire_service', label: 'Tire Service' },
  { value: 'brake_service', label: 'Brake Service' },
  { value: 'engine_service', label: 'Engine Service' },
  { value: 'transmission', label: 'Transmission' },
  { value: 'suspension', label: 'Suspension' },
  { value: 'exhaust', label: 'Exhaust System' },
  { value: 'cooling_system', label: 'Cooling System' },
  { value: 'fuel_system', label: 'Fuel System' },
  { value: 'interior', label: 'Interior' },
  { value: 'exterior', label: 'Exterior' },
  { value: 'safety', label: 'Safety Equipment' },
  { value: 'other', label: 'Other' }
];

// Helper function to get category display name
const getCategoryDisplayName = (categoryValue) => {
  const category = workOrderCategories.find(cat => cat.value === categoryValue);
  return category ? category.label : categoryValue || 'Not Specified';
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return { bg: '#10b981', color: 'white' };
    case 'in progress': return { bg: '#f59e0b', color: 'white' };
    case 'open': return { bg: '#3b82f6', color: 'white' };
    case 'cancelled': return { bg: '#ef4444', color: 'white' };
    default: return { bg: '#e5e7eb', color: '#374151' };
  }
};

const getStatusIcon = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return <CheckCircle sx={{ fontSize: 16 }} />;
    case 'in progress': return <Build sx={{ fontSize: 16 }} />;
    case 'open': return <Schedule sx={{ fontSize: 16 }} />;
    case 'cancelled': return <Warning sx={{ fontSize: 16 }} />;
    default: return <Schedule sx={{ fontSize: 16 }} />;
  }
};

const columns = [
  { id: 'title', label: 'Title' },
  { id: 'category', label: 'Category' },
  { id: 'vehicle', label: 'Vehicle' },
  { id: 'date', label: 'Date' },
  { id: 'createdAt', label: 'Created' },
  { id: 'inspector', label: 'Inspector' },
  { id: 'notes', label: 'Notes' },
  { id: 'checklist', label: 'Checklist' },
  { id: 'workOrders', label: 'Work Orders' },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const InspectionList = ({ onEdit }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get pagination and filter state from URL
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const itemsPerPage = parseInt(searchParams.get('limit')) || 10;
  const search = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || 'All';
  const categoryFilter = searchParams.get('category') || 'All';

  // Update URL parameters
  const updateURLParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== 'All' && value !== '') {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  // Handle filter changes
  const handleSearchChange = (value) => {
    updateURLParams({ search: value, page: 1 });
  };

  const handleStatusChange = (value) => {
    updateURLParams({ status: value, page: 1 });
  };

  const handleCategoryChange = (value) => {
    updateURLParams({ category: value, page: 1 });
  };

  const handlePageChange = (page) => {
    updateURLParams({ page });
  };

  const handleItemsPerPageChange = (limit) => {
    updateURLParams({ limit, page: 1 });
  };

  const { data, loading, error, refetch } = useQuery(INSPECTIONS_LIST, {
    variables: { 
      first: itemsPerPage,
      skip: (currentPage - 1) * itemsPerPage,
      sort: { createdAt: 'DESC' } 
    }
  });
  const [deleteInspection] = useMutation(INSPECTION_DELETE, { onCompleted: () => refetch() });
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, inspection: null });
  const [expandedRow, setExpandedRow] = useState(null);
  const navigate = useNavigate();

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleEdit = (inspection) => {
    if (onEdit) {
      onEdit(inspection);
    } else {
      navigate(`/fleet/inspections/${inspection.id}`);
    }
  };

  const handleAdd = () => navigate('/fleet/inspections/new');

  const handleDeleteClick = (inspection) => setDeleteDialog({ open: true, inspection });
  
  const handleDeleteConfirm = async () => {
    await deleteInspection({ variables: { filter: { id: deleteDialog.inspection.id } } });
    setDeleteDialog({ open: false, inspection: null });
  };
  
  const handleDeleteCancel = () => setDeleteDialog({ open: false, inspection: null });

  const statusOptions = ['All', 'Open', 'In Progress', 'Completed', 'Cancelled'];

  const filteredInspections = useMemo(() => {
    if (!data?.inspectionsList?.items) return [];
    const inspections = data.inspectionsList.items;
    return inspections.filter((inspection) => {
      const matchesSearch = 
        inspection.title?.toLowerCase().includes(search.toLowerCase()) ||
        inspection.category?.toLowerCase().includes(search.toLowerCase()) ||
        inspection.notes?.toLowerCase().includes(search.toLowerCase()) ||
        inspection.inspector?.toLowerCase().includes(search.toLowerCase()) ||
        (inspection.inspections?.make && inspection.inspections.make.toLowerCase().includes(search.toLowerCase())) ||
        (inspection.inspections?.model && inspection.inspections.model.toLowerCase().includes(search.toLowerCase()));
      
      // Filter by work order status if any work orders exist
      const hasWorkOrders = inspection.workOrders?.items?.length > 0;
      const matchesStatus = statusFilter === 'All' || 
        (hasWorkOrders && inspection.workOrders.items.some(wo => wo.status === statusFilter));
      
      // Filter by work order category if any work orders exist
      const matchesCategory = categoryFilter === 'All' || 
        (hasWorkOrders && inspection.workOrders.items.some(wo => wo.category === categoryFilter));
      
      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [data, search, statusFilter, categoryFilter]);

  const sortedRows = [...filteredInspections].sort(getComparator(order, orderBy));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Card sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">Error loading inspections.</Typography>
      </Card>
    );
  }

  return (
    <Box>
      {/* Search and Filters */}
      <Card sx={{ 
        mb: 3, 
        borderRadius: 3, 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Search & Filters
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              label="Search Inspections"
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder="Search by title, category, notes, inspector, or vehicle"
              size="small"
              sx={{ minWidth: 300, flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Work Order Status"
              value={statusFilter}
              onChange={e => handleStatusChange(e.target.value)}
              size="small"
              sx={{ minWidth: 150 }}
            >
              {statusOptions.map(option => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Work Order Category"
              value={categoryFilter}
              onChange={e => handleCategoryChange(e.target.value)}
              size="small"
              sx={{ minWidth: 180 }}
            >
              {workOrderCategories.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleAdd}
              sx={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                }
              }}
            >
              Add Inspection
            </Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Found {data?.inspectionsList?.count || 0} inspections
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Inspection Table */}
      <Card sx={{ 
        borderRadius: 3, 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden'
      }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Vehicle</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>
                  <TableSortLabel
                    active={orderBy === 'createdAt'}
                    direction={orderBy === 'createdAt' ? order : 'asc'}
                    onClick={() => handleSort('createdAt')}
                  >
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Inspector</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Notes</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Checklist</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Work Orders</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Actions</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((row) => {
                const vehicle = Array.isArray(row.inspections) ? row.inspections[0] : row.inspections;
                const workOrdersCount = row.workOrders?.items?.length || 0;
                const checklistItems = row.checklist ? Object.keys(row.checklist).length : 0;
                
                return (
                  <React.Fragment key={row.id}>
                    <TableRow 
                      hover 
                      onClick={() => handleEdit(row)} 
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'rgba(16, 185, 129, 0.04)',
                          transform: 'scale(1.01)',
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar 
                            sx={{ 
                              bgcolor: '#6366f1', 
                              width: 32, 
                              height: 32, 
                              mr: 2,
                              boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)'
                            }}
                          >
                            <DirectionsCar sx={{ fontSize: 16 }} />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {vehicle ? `${vehicle.make} ${vehicle.model}` : 'No vehicle'}
                            </Typography>
                            {vehicle?.year && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                {vehicle.year}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar 
                            sx={{ 
                              bgcolor: '#10b981', 
                              width: 24, 
                              height: 24, 
                              mr: 1,
                              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                            }}
                          >
                            <CalendarToday sx={{ fontSize: 12 }} />
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {row.date ? dayjs(row.date).format('MMM DD, YYYY') : 'No date'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar 
                            sx={{ 
                              bgcolor: '#8b5cf6', 
                              width: 24, 
                              height: 24, 
                              mr: 1,
                              boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)'
                            }}
                          >
                            <Schedule sx={{ fontSize: 12 }} />
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {row.createdAt ? dayjs(row.createdAt).format('MMM DD, YYYY') : 'No date'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar 
                            sx={{ 
                              bgcolor: '#f59e0b', 
                              width: 24, 
                              height: 24, 
                              mr: 1,
                              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)'
                            }}
                          >
                            <Person sx={{ fontSize: 12 }} />
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {row.inspector || 'No inspector'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {row.notes ? (row.notes.length > 50 ? `${row.notes.substring(0, 50)}...` : row.notes) : 'No notes'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {checklistItems > 0 ? (
                            <Chip
                              label={`${checklistItems} items`}
                              color="primary"
                              size="small"
                              onClick={e => {
                                e.stopPropagation();
                                setExpandedRow(expandedRow === row.id ? null : row.id);
                              }}
                              icon={<ExpandMoreIcon sx={{ transform: expandedRow === row.id ? 'rotate(180deg)' : 'rotate(0deg)' }} />}
                              sx={{ cursor: 'pointer' }}
                            />
                          ) : (
                            <Typography variant="caption">No checklist</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar 
                            sx={{ 
                              bgcolor: '#ef4444', 
                              width: 24, 
                              height: 24, 
                              mr: 1,
                              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)'
                            }}
                          >
                            <Assignment sx={{ fontSize: 12 }} />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {workOrdersCount} work order{workOrdersCount !== 1 ? 's' : ''}
                            </Typography>
                            {workOrdersCount > 0 && (
                              <Chip
                                label="View"
                                size="small"
                                color="error"
                                onClick={e => {
                                  e.stopPropagation();
                                  setExpandedRow(expandedRow === row.id ? null : row.id);
                                }}
                                sx={{ mt: 0.5, cursor: 'pointer' }}
                              />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Edit Inspection">
                            <IconButton 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(row);
                              }} 
                              size="small" 
                              sx={{
                                color: '#10b981',
                                '&:hover': {
                                  bgcolor: 'rgba(16, 185, 129, 0.1)'
                                }
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Inspection">
                            <IconButton 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(row);
                              }} 
                              size="small" 
                              sx={{
                                color: '#ef4444',
                                '&:hover': {
                                  bgcolor: 'rgba(239, 68, 68, 0.1)'
                                }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {(checklistItems > 0) || workOrdersCount > 0 ? (
                          <IconButton
                            size="small"
                            onClick={e => {
                              e.stopPropagation();
                              setExpandedRow(expandedRow === row.id ? null : row.id);
                            }}
                          >
                            <ExpandMoreIcon sx={{ transform: expandedRow === row.id ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                          </IconButton>
                        ) : null}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
                        <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 2, p: 2, border: '1px solid #e2e8f0', borderRadius: 2, background: '#f9fafb' }}>
                            {/* Checklist Section */}
                            {checklistItems > 0 && (
                              <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  Inspection Checklist
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                  {Object.entries(row.checklist).map(([key, value]) => (
                                    <Chip
                                      key={key}
                                      label={`${key}: ${value || 'N/A'}`}
                                      color={
                                        value === 'yes'
                                          ? 'success'
                                          : value === 'no'
                                          ? 'error'
                                          : value === 'issue'
                                          ? 'warning'
                                          : 'default'
                                      }
                                      variant="outlined"
                                      sx={{ minWidth: 120 }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}

                            {/* Work Orders Section */}
                            {workOrdersCount > 0 && (
                              <Box>
                                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  Linked Work Orders
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  {row.workOrders.items.map((workOrder) => {
                                    const daysOpen = workOrder.dateOpened ? dayjs().diff(dayjs(workOrder.dateOpened), 'day') : 0;
                                    return (
                                      <Card key={workOrder.id} sx={{ p: 2, border: '1px solid #e2e8f0' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Title sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                              {workOrder.title || `Work Order #${workOrder.id}`}
                                            </Typography>
                                          </Box>
                                          <Chip
                                            icon={getStatusIcon(workOrder.status)}
                                            label={workOrder.status || 'Open'}
                                            size="small"
                                            sx={{
                                              bgcolor: getStatusColor(workOrder.status).bg,
                                              color: getStatusColor(workOrder.status).color,
                                              fontWeight: 600,
                                              '& .MuiChip-icon': {
                                                color: getStatusColor(workOrder.status).color
                                              }
                                            }}
                                          />
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                                          <Chip
                                            label={getCategoryDisplayName(workOrder.category)}
                                            size="small"
                                            sx={{
                                              bgcolor: '#e3f2fd',
                                              color: '#1976d2',
                                              fontWeight: 500
                                            }}
                                          />
                                          {workOrder.dateOpened && (
                                            <Chip
                                              label={`Opened: ${dayjs(workOrder.dateOpened).format('MMM DD, YYYY')}`}
                                              size="small"
                                              variant="outlined"
                                            />
                                          )}
                                          <Chip
                                            label={`${daysOpen} days open`}
                                            size="small"
                                            sx={{
                                              bgcolor: daysOpen > 30 ? '#fee2e2' : daysOpen > 14 ? '#fef3c7' : '#d1fae5',
                                              color: daysOpen > 30 ? '#dc2626' : daysOpen > 14 ? '#d97706' : '#059669',
                                              fontWeight: 600
                                            }}
                                          />
                                        </Box>

                                        {workOrder.workOrderDetails && (
                                          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                                            <strong>Details:</strong> {workOrder.workOrderDetails}
                                          </Typography>
                                        )}

                                        {workOrder.repairNotes && (
                                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            <strong>Repair Notes:</strong> {workOrder.repairNotes}
                                          </Typography>
                                        )}

                                        <Box sx={{ mt: 1 }}>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigate(`/fleet/work-orders/${workOrder.id}`);
                                            }}
                                            sx={{ color: '#ef4444', borderColor: '#ef4444' }}
                                          >
                                            View Work Order
                                          </Button>
                                        </Box>
                                      </Card>
                                    );
                                  })}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil((data?.inspectionsList?.count || 0) / itemsPerPage)}
          totalItems={data?.inspectionsList?.count || 0}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          itemsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialog.open} 
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: 'white',
          fontWeight: 600
        }}>
          Delete Inspection
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography>
            Are you sure you want to delete this inspection? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleDeleteCancel} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InspectionList; 